import { Flex, VStack } from '@chakra-ui/react';
import { debounce } from 'lodash';
import React from 'react';
import { OptionsType, OptionTypeBase } from 'react-select/src/types';
import apiClient from '../../../canopy-ui-components/api';
import CanopySearch from '../../../canopy-ui-components/components/CanopySelect/CanopySearch';
import CanopySelect from '../../../canopy-ui-components/components/CanopySelect/CanopySelect';
import { useCurrentColors } from '../../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
export type UserDetailsType = {
    userId: number;
    userRoleId?: number;
    adminId?: number;
    adminRoleId?: number;
    userName: any;
    paOrRmName: any;
    deviceType: any;
};

const DEVICE_TYPE = ['desktop', 'tablet', 'mobile'];

interface Props {
    data: UserDetailsType;
    initialData: UserDetailsType;
    mappingsIndex: number;
    globalProps: GlobalProps;
    updateUserDetails: (userDetails: UserDetailsType, index: number) => void;
}
function UserDetails({
    initialData: data,
    mappingsIndex,
    updateUserDetails,
    globalProps,
    ...props
}: Props) {
    const debouncedgetUsers = debounce(
        apiClient.getUsersByName.bind(apiClient),
        300
    );
    const currentColors = useCurrentColors();
    const [adminRoleIds] = React.useState([5, 8]);
    const { t } = globalProps;
    const loadUsers = (
        inputValue: string,
        callback: (options: OptionsType<OptionTypeBase>) => void
    ) => {
        if (!inputValue) {
            return callback([]);
        }
        debouncedgetUsers(inputValue, '4,5,8', callback);
    };

    const loadPaOrRm = (
        inputValue: string,
        callback: (options: OptionsType<OptionTypeBase>) => void
    ) => {
        if (!inputValue) {
            return callback([]);
        }
        debouncedgetUsers(inputValue, '5,8', callback);
    };

    const onUserSelection = (value: any) => {
        const userDetails = {
            ...data,
            userName: value?.username,
            userId: value?.id,
            userRoleId: value?.roleId,
        };

        if (adminRoleIds.includes(value?.roleId)) {
            delete userDetails?.paOrRmName;
            delete userDetails?.adminId;
        }
        updateUserDetails(userDetails, mappingsIndex);
    };

    const onPaOrRmSelection = (value: any) => {
        const userDetails = {
            ...data,
            paOrRmName: value?.username,
            adminId: value?.id,
        };
        updateUserDetails(userDetails, mappingsIndex);
    };

    const onDeviceTypeSelection = (item: any) => {
        const userDetails = {
            ...data,
            deviceType: item?.value,
        };
        updateUserDetails(userDetails, mappingsIndex);
    };

    const deviceTypeOptions = React.useMemo(
        () =>
            DEVICE_TYPE.map((value: string) => ({
                value,
                label: t(`device.${value}`),
            })),
        []
    );

    const selectedDeviceOption = React.useMemo(
        () =>
            deviceTypeOptions.find(
                (type) => type.value === (data?.deviceType || 'desktop')
            ),
        [data?.deviceType]
    );

    return (
        <VStack
            spacing="15px"
            m="10px"
            width="100%">
            <Flex
                textAlign="start"
                width="100%">
                <CanopySearch
                    loadOptions={loadUsers}
                    onChange={onUserSelection}
                    // onInputChange={(value: string) => setInputUserName(value)}
                    placeholder={t('select-user')}
                    isMultiLineOption
                    getOptionLabel={(option: any) =>
                        `${option.displayName} \n ${option.username}`
                    }
                    t={t}
                    getOptionValue={(option: any) => option.username}
                    value={
                        data?.userName
                            ? { value: data?.userName, label: data?.userName }
                            : null
                    }
                    isSearchable={true}
                    loadingMessage={() => `${t('loading')}...`}
                    isClearable={data?.userName}
                />
            </Flex>

            <Flex
                textAlign="start"
                width="100%">
                <CanopySearch
                    isDisabled={adminRoleIds.includes(data?.userRoleId || 4)}
                    isClearable={data?.paOrRmName}
                    loadOptions={loadPaOrRm}
                    onChange={onPaOrRmSelection}
                    // onInputChange={(value: string) => setInputPaOrRmName(value)}
                    placeholder={t('select-pa-or-rm')}
                    isMultiLineOption
                    getOptionLabel={(option: any) =>
                        `${option.displayName} \n ${option.username}`
                    }
                    getOptionValue={(option: any) => option.username}
                    value={
                        data?.paOrRmName
                            ? {
                                  value: data?.paOrRmName,
                                  label: data?.paOrRmName,
                              }
                            : null
                    }
                    t={t}
                    isSearchable={true}
                    loadingMessage={() => `${t('loading')}...`}
                />
            </Flex>
            <Flex
                textAlign="start"
                width="100%">
                <CanopySelect
                    id="deviceType"
                    placeholder={t('ui.mm.select-device-type')}
                    defaultValue={deviceTypeOptions[0]}
                    value={selectedDeviceOption}
                    options={deviceTypeOptions}
                    getOptionLabel={(option: any) => option.label}
                    getOptionValue={(option: any) => option.value}
                    onChange={onDeviceTypeSelection}
                    color={currentColors.brand['10']}></CanopySelect>
            </Flex>
        </VStack>
    );
}
export default UserDetails;
